<template>
  <b-card no-body class="next-maintenance-card h-100" :class="`status-${status}`">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">
        <feather-icon icon="ToolIcon" size="16" class="mr-50" />
        {{ $t('Next Maintenance') }}
      </h5>
      <b-badge v-if="nextMaintenance" :variant="meta.variant" pill>
        {{ $t(meta.labelKey) }}
      </b-badge>
    </div>

    <b-card-body>
      <div v-if="loading" class="text-center py-2">
        <b-spinner small variant="primary" />
      </div>

      <div v-else-if="nextMaintenance">
        <div class="next-names mb-1">
          <div class="text-muted small">{{ $t('Plan') }}</div>
          <div class="font-weight-bold text-truncate" v-b-tooltip.hover :title="nextMaintenance.planName || '-'">
            {{ nextMaintenance.planName || '—' }}
          </div>
          <div class="text-muted small mt-50">{{ $t('Step') }}</div>
          <div class="text-truncate" v-b-tooltip.hover :title="nextMaintenance.name">
            {{ nextMaintenance.name }}
          </div>
        </div>

        <div class="remaining-block mb-1" :class="`text-${meta.variant}`">
          <feather-icon :icon="meta.icon" size="18" class="mr-50" />
          <span>
            {{ nextMaintenance.remaining > 0 ? $t('Remaining:') : $t('Overdue:') }}
            <strong>{{ formatMetric(Math.abs(nextMaintenance.remaining)) }}</strong>
            {{ nextMaintenance.unit }}
          </span>
        </div>

        <b-progress
          :value="nextMaintenance.progress"
          :max="100"
          height="8px"
          :variant="meta.variant"
          class="mb-1"
        />

        <div class="d-flex justify-content-between small text-muted">
          <span>
            {{ $t('Current reading') }}:
            <strong>{{ formatMetric(nextMaintenance.currentValue) }}</strong>
            {{ nextMaintenance.unit }}
          </span>
          <span>
            {{ $t('Scheduled') }}:
            <strong>{{ formatMetric(nextMaintenance.scheduledValue) }}</strong>
            {{ nextMaintenance.unit }}
          </span>
        </div>
      </div>

      <div v-else class="empty-state text-center py-1">
        <feather-icon icon="ToolIcon" size="28" class="text-muted mb-50" />
        <h6 class="mb-25">
          {{ hasMaintenancePlan ? $t('No maintenance scheduled') : $t('No maintenance plan registered') }}
        </h6>
        <p class="text-muted mb-1 small">
          {{ hasMaintenancePlan
            ? $t('No upcoming maintenance has been scheduled')
            : $t('No maintenance plan has been registered for this asset') }}
        </p>
        <b-button v-if="!hasMaintenancePlan" size="sm" variant="primary" @click="$emit('associate')">
          {{ $t('Associate plan') }}
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BBadge, BButton, BCard, BCardBody, BProgress, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import { computed } from '@vue/composition-api'
import { STATUS_META } from '../maintenanceUtils'

export default {
  name: 'NextMaintenanceCard',
  components: {
    BBadge, BButton, BCard, BCardBody, BProgress, BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    nextMaintenance: { type: Object, default: null },
    hasMaintenancePlan: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    formatMetric: { type: Function, required: true },
  },
  setup(props) {
    const status = computed(() => props.nextMaintenance?.status || 'onTime')
    const meta = computed(() => STATUS_META[status.value] || STATUS_META.onTime)

    return { status, meta }
  },
}
</script>

<style lang="scss" scoped>
.next-maintenance-card {
  border-left: 4px solid #28c76f;

  &.status-dueSoon { border-left-color: #ff9f43; }
  &.status-overdue { border-left-color: #ea5455; }
}

.remaining-block {
  font-size: 1.05rem;
  display: flex;
  align-items: center;
}

.empty-state {
  color: #6e6b7b;
}
</style>
